import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { refLink } from "@/lib/referral";
import { DollarSign, Save, Copy, Pencil } from "lucide-react";
import { inputCls, outlineBtn } from "./shared";

const AffiliatesTab = ({ commission, setCommission, saveCommission, affiliates, editAffiliate, openAffDetail, copyText }) => (
  <>
    <div className="bg-white border border-[var(--ir-border)] rounded-lg soft-shadow p-5 mb-4 flex flex-wrap items-end gap-5">
      <div>
        <Label className="text-[var(--ir-text)] flex items-center gap-2"><DollarSign className="w-4 h-4 text-[var(--ir-orange)]" />Commission settings</Label>
        <p className="text-[var(--ir-muted)] text-xs mt-1">Default rate applied to new affiliates. Each affiliate can override this on their own record. Changing this never affects commissions already earned.</p>
      </div>
      <div>
        <Label className="text-[var(--ir-muted)] text-xs">Membership fee ($)</Label>
        <Input type="number" value={commission.membership_fee} onChange={(e) => setCommission({ ...commission, membership_fee: e.target.value })} className={`w-32 ${inputCls}`} data-testid="membership-fee-input" />
      </div>
      <div>
        <Label className="text-[var(--ir-muted)] text-xs">Commission (%)</Label>
        <Input type="number" value={commission.percent} onChange={(e) => setCommission({ ...commission, percent: e.target.value })} className={`w-28 ${inputCls}`} data-testid="commission-percent-input" />
      </div>
      <Button onClick={saveCommission} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none" data-testid="save-commission-btn">
        <Save className="w-4 h-4 mr-2" />Save
      </Button>
    </div>
    <div className="bg-white border border-[var(--ir-border)] rounded-lg overflow-x-auto soft-shadow">
      <Table>
        <TableHeader>
          <TableRow className="border-[var(--ir-border)] hover:bg-transparent">
            <TableHead className="text-[var(--ir-muted)]">Affiliate</TableHead>
            <TableHead className="text-[var(--ir-muted)]">Referral Link</TableHead>
            <TableHead className="text-[var(--ir-muted)] text-center">Rate</TableHead>
            <TableHead className="text-[var(--ir-muted)] text-center">Clicks</TableHead>
            <TableHead className="text-[var(--ir-muted)] text-center">Sign-ups</TableHead>
            <TableHead className="text-[var(--ir-muted)] text-center">Approved</TableHead>
            <TableHead className="text-[var(--ir-muted)] text-right">Owed</TableHead>
            <TableHead className="text-[var(--ir-muted)] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {affiliates.length === 0 && (
            <TableRow className="border-[var(--ir-border)]"><TableCell colSpan={8} className="text-center text-[var(--ir-muted)] py-10">No affiliates yet. Set a member's Affiliate field to Yes to enroll them.</TableCell></TableRow>
          )}
          {affiliates.map((a) => (
            <TableRow key={a.id} className="border-[var(--ir-border)] hover:bg-[var(--ir-surface2)]/50" data-testid={`affiliate-row-${a.id}`}>
              <TableCell>
                <div className="font-medium text-[var(--ir-text)]">{a.name}</div>
                <div className="text-[var(--ir-muted)] text-sm">{a.email}</div>
                {a.promo_code && <div className="text-[var(--ir-orange)] text-xs mt-0.5">Promo: {a.promo_code}</div>}
                {a.referred_by_name && <div className="text-[var(--ir-blue)] text-xs mt-0.5">Referred by {a.referred_by_name}</div>}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-[var(--ir-blue)] max-w-[200px] truncate" data-testid={`affiliate-link-${a.id}`}>{refLink(a.affiliate_code)}</code>
                  <Button size="sm" variant="outline" onClick={() => copyText(refLink(a.affiliate_code), "Referral link")} className={outlineBtn} title="Copy link" data-testid={`copy-link-btn-${a.id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </TableCell>
              <TableCell className="text-center text-[var(--ir-text)]" data-testid={`affiliate-rate-${a.id}`}>{a.commission_percent}%</TableCell>
              <TableCell className="text-center text-[var(--ir-text)]">{a.clicks}</TableCell>
              <TableCell className="text-center text-[var(--ir-text)]">{a.signups}</TableCell>
              <TableCell className="text-center text-[var(--ir-text)]">{a.approved_referrals}</TableCell>
              <TableCell className="text-right font-medium text-[var(--ir-text)]">${a.commission_owed.toFixed(2)}</TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => editAffiliate(a)} className={outlineBtn} data-testid={`edit-affiliate-btn-${a.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                  <Button size="sm" onClick={() => openAffDetail(a)} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none h-8" data-testid={`view-affiliate-btn-${a.id}`}>Manage</Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </>
);

export default AffiliatesTab;
